(() => {
  const REQUIRED_STRINGING_HANDLES = new Set(['acc-decoration-313', 'acc-decoration-314']);
  const STRINGING_REQUIREMENT_HASH = '#stringing-material-required';

  const getCartUrl = (suffix = '') => {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}cart${suffix}`.replace(/\/+/g, '/');
  };

  const fetchCart = async () => {
    const response = await fetch(getCartUrl('.js'), {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) throw new Error('Unable to check the cart.');
    return response.json();
  };

  const getCartItemHandle = (item) => {
    const explicitHandle = item?.handle || item?.product_handle || '';
    if (explicitHandle) return String(explicitHandle).toLowerCase();

    const urlPath = String(item?.url || '').split('?')[0];
    const match = urlPath.match(/\/products\/([^/]+)$/);
    return match ? decodeURIComponent(match[1]).toLowerCase() : '';
  };

  const cartHasStringingMaterial = (cart) =>
    Array.isArray(cart?.items) &&
    cart.items.some(
      (item) => Number(item?.quantity || 0) > 0 && REQUIRED_STRINGING_HANDLES.has(getCartItemHandle(item))
    );

  const pulseStringingRequirement = ({ scroll = true } = {}) => {
    const banner = document.querySelector('[data-stringing-requirement]');
    if (!banner) return false;

    banner.classList.remove('is-attention');
    void banner.offsetWidth;
    banner.classList.add('is-attention');
    window.setTimeout(() => banner.classList.remove('is-attention'), 500);

    const bannerBounds = banner.getBoundingClientRect();
    const isVisible = bannerBounds.top >= 0 && bannerBounds.bottom <= window.innerHeight;
    if (scroll && !isVisible) {
      banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    banner.focus({ preventScroll: true });
    return true;
  };

  const showStringingRequirement = (sourceForm) => {
    const banner = document.querySelector('[data-stringing-requirement]');
    if (banner) {
      if (sourceForm?.id === 'CartDrawer-Form') {
        document.querySelector('cart-drawer')?.close?.();
      } else if (sourceForm?.id === 'cart-notification-form') {
        document.querySelector('cart-notification')?.close?.();
      }
      window.requestAnimationFrame(() => pulseStringingRequirement());
      return;
    }
    window.location.href = `${getCartUrl()}${STRINGING_REQUIREMENT_HASH}`;
  };

  const getFormNoteInput = (form) => {
    if (form.id === 'cart') {
      return document.querySelector('#Cart-note, textarea[name="note"][form="cart"]');
    }
    if (form.id === 'CartDrawer-Form') {
      return document.querySelector('#CartDrawer-Note, textarea[name="note"][form="CartDrawer-Form"]');
    }
    return form.querySelector('textarea[name="note"]');
  };

  const syncCheckoutForm = async (form) => {
    const updateInputs = Array.from(form.querySelectorAll('input[name="updates[]"][data-index]'));
    const updates = {};

    updateInputs.forEach((input) => {
      const row = input.closest('.cart-item');
      const key = row?.dataset?.cartItemKey || row?.dataset?.diyKey || '';
      const variantId = input.dataset.quantityVariantId || '';
      const value = parseInt(input.value || '0', 10);
      const quantity = Number.isFinite(value) ? Math.max(0, value) : 0;

      if (key) {
        updates[key] = quantity;
      } else if (variantId) {
        updates[variantId] = quantity;
      }
    });

    if (Object.keys(updates).length === 0) return;

    const noteInput = getFormNoteInput(form);
    const payload = { updates };
    if (noteInput) payload.note = noteInput.value || '';

    const response = await fetch(getCartUrl('/update.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error('Failed to sync cart before checkout.');
  };

  const handleCheckoutSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const submitter =
      event.submitter || (document.activeElement?.name === 'checkout' ? document.activeElement : null);
    if (!submitter || submitter.name !== 'checkout') return;

    event.preventDefault();
    if (form.dataset.checkoutSyncing === 'true') return;
    form.dataset.checkoutSyncing = 'true';

    const originalText = submitter.textContent;
    submitter.disabled = true;
    submitter.setAttribute('aria-disabled', 'true');
    submitter.textContent = 'Checking...';

    try {
      await syncCheckoutForm(form);
      const cart = await fetchCart();
      if (!cartHasStringingMaterial(cart)) {
        showStringingRequirement(form);
        return;
      }

      window.location.href = '/checkout';
    } catch (error) {
      console.error(error);
      showStringingRequirement(form);
    } finally {
      form.dataset.checkoutSyncing = '';
      if (submitter.isConnected) {
        submitter.textContent = originalText;
        submitter.disabled = false;
        submitter.removeAttribute('aria-disabled');
      }
    }
  };

  const getCartSections = () => {
    const sections = new Map();
    const cartItems = document.querySelector('cart-items');
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartItems?.getSectionsToRender) {
      cartItems.getSectionsToRender().forEach((section) => {
        sections.set(section.section, section);
      });
    }

    if (cartDrawer?.getSectionsToRender) {
      cartDrawer.getSectionsToRender().forEach((section) => {
        sections.set(section.section, section);
      });
    }

    return Array.from(sections.values());
  };

  const renderSections = (parsedState, sections) => {
    sections.forEach((section) => {
      const container = document.getElementById(section.id);
      const sectionHtml = parsedState?.sections?.[section.section];
      if (!container || !sectionHtml) return;

      const elementToReplace = container.querySelector(section.selector) || container;
      const parsedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const sourceElement = parsedDocument.querySelector(section.selector);
      if (!sourceElement) return;
      elementToReplace.innerHTML = sourceElement.innerHTML;
    });
  };

  const syncCartEmptyState = (itemCount) => {
    const isEmpty = itemCount === 0;
    const cartItems = document.querySelector('cart-items');
    if (cartItems) cartItems.classList.toggle('is-empty', isEmpty);

    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) cartDrawer.classList.toggle('is-empty', isEmpty);

    const titleCount = document.querySelector('.cart-title__count');
    if (titleCount) titleCount.textContent = `(${itemCount})`;

    const checkoutButton = document.querySelector('#checkout[name="checkout"][form="cart"]');
    if (checkoutButton) {
      checkoutButton.disabled = isEmpty;
      checkoutButton.setAttribute('aria-disabled', isEmpty ? 'true' : 'false');
    }
  };

  const addStringingMaterial = async (button) => {
    const variantId = Number(button.dataset.variantId || 0);
    if (!variantId || button.disabled) return;

    const sections = getCartSections();
    const label = button.querySelector('[data-stringing-button-label]');
    const originalText = label?.textContent || button.textContent;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (label) label.textContent = 'Adding...';

    try {
      const response = await fetch(getCartUrl('/add.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sections.map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      });

      const state = await response.json();
      if (!response.ok || state.status) {
        throw new Error(state.description || 'Unable to add the stringing material.');
      }

      renderSections(state, sections);
      const cart = await fetchCart();
      syncCartEmptyState(Number(cart.item_count || 0));

      const updatedBanner = document.querySelector('[data-stringing-requirement]');
      updatedBanner?.focus({ preventScroll: true });
    } catch (error) {
      console.error(error);
      if (button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (label) label.textContent = originalText;
      }
    }
  };

  const removeDiySet = (parentId, trigger) => {
    const rows = Array.from(document.querySelectorAll('[data-diy-key]')).filter(
      (row) => row.dataset.diyParentId === parentId
    );
    if (!rows.length) return;

    const updates = {};
    rows.forEach((row) => {
      const key = row.dataset.diyKey;
      if (key) updates[key] = 0;
    });

    const sections = getCartSections();
    const body = JSON.stringify({
      updates,
      sections: sections.map((section) => section.section),
      sections_url: window.location.pathname,
    });

    const config = typeof fetchConfig === 'function' ? fetchConfig() : { headers: {} };
    fetch(`${routes.cart_update_url}`, { ...config, body })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        renderSections(parsedState, sections);
        syncCartEmptyState(parsedState.item_count || 0);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  document.addEventListener('submit', handleCheckoutSubmit, true);

  if (window.location.hash === STRINGING_REQUIREMENT_HASH) {
    window.requestAnimationFrame(() => {
      pulseStringingRequirement();
      window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}`);
    });
  }

  document.addEventListener('click', (event) => {
    const quickAddButton = event.target.closest('[data-stringing-quick-add]');
    if (quickAddButton) {
      event.preventDefault();
      addStringingMaterial(quickAddButton);
      return;
    }

    const button = event.target.closest('[data-diy-remove]');
    if (!button) return;
    const parentId = button.dataset.diyParentId;
    if (!parentId) return;
    event.preventDefault();
    removeDiySet(parentId, button);
  });
})();
